/**
 * MRI records, segmentations and overlays (`app/services/mri_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import type { OverlayRole } from '@brainfocus/web/domain/neuroimage/overlay_roles';
import { Observable } from 'rxjs';
import { volumeStore } from '@brainfocus/web/api/volume_store';

export function uploadMri(payload: UploadMriPayload): Observable<client.ResponseProgress<{
    status: string;
    message?: string;
    // Id of the record the server created. Returned so the client can delete
    // it if the user cancels after the body already reached the server (#717).
    mri_id?: number;
}>> {
    let url: string;

    const queryParams = new URLSearchParams({
        deface: String(payload.deface),
        mriName: payload.mriName,
        ...(payload.recordingId ? { recordingId: String(payload.recordingId) } : {}),
        ...(payload.uploadRef ? { uploadRef: payload.uploadRef } : {}),
    }).toString();

    if (payload.patientId) {
        url = `/api/patient/${payload.patientId}/upload-mri?${queryParams}`;
    } else {
        throw new Error('No patient ID');
    }

    const body = {
        mri: payload.mri,
        coords: payload.coords,
    };

    return client.requestWithProgress(url, body);
}

export async function getPatientMris(patientId: number): Promise<MriRecord[]> {
    const url = `/api/patient/${patientId}/mris`;
    const response = await client.request('GET', url) as { mri_records: MriRecord[] };
    return response.mri_records as MriRecord[];
}

/**
 * Delete an MRI record (uploader or SuperAdmin only)
 */
export async function deleteMri(mriId: number): Promise<void> {
    await client.request('DELETE', `/api/mri/${mriId}`);
    volumeStore.invalidateMri(mriId);
}

/**
 * Clean up a cancelled upload by its client correlation ref (#717).
 * Idempotent: succeeds whether or not a record was created for the ref.
 */
export async function deleteMriByUploadRef(patientId: number, uploadRef: string): Promise<void> {
    await client.request('DELETE', `/api/patient/${patientId}/mri/by-upload-ref/${encodeURIComponent(uploadRef)}`);
}

export async function uploadSegmentation(mriId: number, file: File): Promise<{ status: string }> {
    const formData = new FormData();
    formData.append('seg', file);
    // Raw: the shared gates apply, and the caller reads its own error text (#1165).
    const response = await client.request('POST', `/api/mri/${mriId}/upload-seg`, formData, { raw: true });
    if (!response.ok) {
        const err = await response.json().catch((e) => {
            console.error('Failed to parse segmentation upload error response:', e);
            return { error: 'Upload failed' };
        });
        throw new Error(err.error || 'Upload failed');
    }
    // The viewers must reload this MRI with the new volume (#682, #822).
    volumeStore.invalidateMri(mriId);
    return response.json();
}

/**
 * Attach a coregistered overlay volume to an MRI record, by role (#502 CT, #989 post-implant
 * MRI). It is attached (like a segmentation) rather than listed as a separate record; the
 * viewers auto-overlay it. Mirrors uploadSegmentation (FormData, raw response).
 */
export async function uploadOverlay(mriId: number, role: OverlayRole, file: File): Promise<{ status: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await client.request('POST', `/api/mri/${mriId}/overlay/${role}`, formData, { raw: true });
    if (!response.ok) {
        const err = await response.json().catch((e) => {
            console.error('Failed to parse overlay upload error response:', e);
            return { error: 'Upload failed' };
        });
        throw new Error(err.error || 'Upload failed');
    }
    // The viewers must reload this MRI with the new volume (#682, #822).
    volumeStore.invalidateMri(mriId);
    return response.json();
}

/**
 * Attach a post-implantation scan without saying what it is: the server tells CT from
 * MRI by its intensities and returns the role it attached, and whether it was sure
 * (#989 D11). Same raw request as `uploadOverlay`.
 */
export async function uploadPostImplantation(mriId: number, file: File): Promise<{ status: string; role: OverlayRole; confident: boolean }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await client.request('POST', `/api/mri/${mriId}/overlay`, formData, { raw: true });
    if (!response.ok) {
        const err = await response.json().catch((e) => {
            console.error('Failed to parse post-implantation upload error response:', e);
            return { error: 'Upload failed' };
        });
        throw new Error(err.error || 'Upload failed');
    }
    // The viewers must reload this MRI with the new volume (#682, #822).
    volumeStore.invalidateMri(mriId);
    return response.json();
}

/** Relabel an attached post-implantation scan CT <-> MRI without re-uploading it (#989 D11). */
export async function changeOverlayRole(mriId: number, role: OverlayRole, newRole: OverlayRole): Promise<{ status: string; role: OverlayRole }> {
    const result = await client.request('PATCH', `/api/mri/${mriId}/overlay/${role}`, { role: newRole }) as { status: string; role: OverlayRole };
    volumeStore.invalidateMri(mriId);
    return result;
}

/** Remove the overlay of that role from an MRI record (#989). */
export async function deleteOverlay(mriId: number, role: OverlayRole): Promise<void> {
    await client.request('DELETE', `/api/mri/${mriId}/overlay/${role}`);
    volumeStore.invalidateMri(mriId);
}

export async function deleteSegmentation(mriId: number): Promise<void> {
    await client.request('DELETE', `/api/mri/${mriId}/seg`);
    volumeStore.invalidateMri(mriId);
}

export async function assignMriToAnalysis(analysisId: number, mri_id: number) {
    const url = `/api/set-mri/${mri_id}`;
    const body = {
        recordingId: analysisId,
    };
    const response = await client.request('POST', url, body);
    return response;
}

export interface UploadMriPayload {
    recordingId?: number;
    patientId: number;
    deface: boolean;
    mri: File;
    coords: File;
    mriName: string;
    // Client-generated correlation id so a cancelled upload can be cleaned up
    // reliably via deleteMriByUploadRef, regardless of the server-assigned id
    // (which an aborted request never sees) (#717).
    uploadRef?: string;
}

export interface MriRecord {
    id: number;
    name: string;
    seg_ref?: string | null;
}
